import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollText, Send, MessageSquare, CheckCircle2, XCircle, Receipt, Sparkles, Clock, ArrowRight } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

interface InquiryRow {
  id: string;
  full_name: string;
  email: string;
  status: 'new' | 'contacted' | 'qualified' | 'won' | 'lost';
  created_at: string;
  updated_at: string;
  approved_at: string | null;
  message: string | null;
  package_id: string | null;
  subscription_id: string | null;
  advertising_packages: { name: string; tier_label: string | null; price_etb: number; price_currency: string } | null;
  subscriptions: { id: string; status: string; started_at: string; expires_at: string } | null;
}

interface InvoiceRow {
  id: string;
  invoice_number: string;
  amount_etb: number;
  status: string;
  paid_at: string | null;
  created_at: string;
  inquiry_id: string | null;
  checkout_url: string | null;
  provider: string;
}

type StepState = 'done' | 'active' | 'todo' | 'skipped';
interface Step {
  key: string;
  label: string;
  description: string;
  icon: any;
  state: StepState;
  at?: string | null;
}

function buildTimeline(inq: InquiryRow, invoice: InvoiceRow | undefined): Step[] {
  const declined = inq.status === 'lost';
  const won = inq.status === 'won';
  const contacted = ['contacted', 'qualified', 'won'].includes(inq.status);
  const qualified = ['qualified', 'won'].includes(inq.status);

  const submitDone = true;
  const reviewState: StepState = won ? 'done' : declined ? 'skipped' : contacted ? 'done' : 'active';
  const qualifyState: StepState = won ? 'done' : declined ? 'skipped' : qualified ? 'done' : contacted ? 'active' : 'todo';
  const decisionState: StepState = won ? 'done' : declined ? 'done' : qualified ? 'active' : 'todo';
  const provisionState: StepState = won ? 'done' : declined ? 'skipped' : 'todo';

  return [
    { key: 'submit', label: 'Submitted', description: 'Your request was sent to our advertising team.', icon: Send, state: submitDone ? 'done' : 'todo', at: inq.created_at },
    { key: 'review', label: 'Under Review', description: 'A specialist is reviewing your request.', icon: MessageSquare, state: reviewState, at: contacted ? inq.updated_at : null },
    { key: 'qualify', label: 'Qualified', description: 'Your business needs were verified for the package.', icon: CheckCircle2, state: qualifyState, at: qualified ? inq.updated_at : null },
    {
      key: 'decision',
      label: declined ? 'Declined' : 'Approved',
      description: declined
        ? 'Our team did not approve this request. Please contact us for details.'
        : 'Your subscription was approved.',
      icon: declined ? XCircle : CheckCircle2,
      state: decisionState,
      at: inq.approved_at || (won || declined ? inq.updated_at : null),
    },
    {
      key: 'provision',
      label: 'Credits Provisioned',
      description: invoice
        ? `Invoice ${invoice.invoice_number} · ${invoice.status}`
        : 'Your package credits are now active in your account.',
      icon: Sparkles,
      state: provisionState,
      at: inq.subscriptions?.started_at,
    },
  ];
}

const STEP_TONE: Record<StepState, { ring: string; bg: string; text: string }> = {
  done: { ring: 'ring-green-600/20', bg: 'bg-green-600 text-white', text: 'text-foreground' },
  active: { ring: 'ring-primary/30', bg: 'bg-primary text-primary-foreground animate-pulse', text: 'text-foreground' },
  todo: { ring: 'ring-muted', bg: 'bg-muted text-muted-foreground', text: 'text-muted-foreground' },
  skipped: { ring: 'ring-muted', bg: 'bg-muted text-muted-foreground line-through', text: 'text-muted-foreground' },
};

const STATUS_BADGE: Record<InquiryRow['status'], { variant: 'default' | 'secondary' | 'outline' | 'destructive'; label: string }> = {
  new: { variant: 'secondary', label: 'Submitted' },
  contacted: { variant: 'secondary', label: 'Under review' },
  qualified: { variant: 'secondary', label: 'Qualified' },
  won: { variant: 'default', label: 'Approved · Active' },
  lost: { variant: 'destructive', label: 'Declined' },
};

export default function MyPackageRequests() {
  const { user } = useAuth();

  const { data: inquiries, isLoading } = useQuery({
    queryKey: ['my-inquiries-full', user?.id, user?.email],
    enabled: !!user,
    queryFn: async () => {
      const email = user!.email?.toLowerCase();
      const { data, error } = await supabase
        .from('package_inquiries' as any)
        .select(`
          id, full_name, email, status, created_at, updated_at, approved_at, message,
          package_id, subscription_id,
          advertising_packages(name, tier_label, price_etb, price_currency),
          subscriptions(id, status, started_at, expires_at)
        `)
        .or(`user_id.eq.${user!.id},email.eq.${email}`)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return ((data as any[]) || []) as InquiryRow[];
    },
  });

  const inquiryIds = (inquiries || []).map((i) => i.id);

  const { data: invoices } = useQuery({
    queryKey: ['my-inquiry-invoices', inquiryIds.join(',')],
    enabled: inquiryIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from('payment_invoices' as any)
        .select('id, invoice_number, amount_etb, status, paid_at, created_at, inquiry_id, checkout_url, provider')
        .in('inquiry_id', inquiryIds);
      return ((data as any[]) || []) as InvoiceRow[];
    },
  });

  const invoiceByInquiry = new Map<string, InvoiceRow>();
  (invoices || []).forEach((inv) => { if (inv.inquiry_id) invoiceByInquiry.set(inv.inquiry_id, inv); });

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ScrollText className="h-6 w-6 text-primary" /> My Package Requests
          </h1>
          <p className="text-sm text-muted-foreground">Track every request from submission to approval, with full timeline.</p>
        </div>
        <Button asChild variant="outline" className="gap-1">
          <Link to="/dashboard/subscription">Browse packages <ArrowRight className="h-3.5 w-3.5" /></Link>
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : !inquiries || inquiries.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center space-y-3">
            <ScrollText className="h-10 w-10 text-muted-foreground mx-auto" />
            <p className="font-medium">No package requests yet</p>
            <p className="text-sm text-muted-foreground">When you request a package from the Subscription Center, you'll see its progress here.</p>
            <Button asChild><Link to="/dashboard/subscription">Explore packages</Link></Button>
          </CardContent>
        </Card>
      ) : (
        inquiries.map((inq) => {
          const invoice = invoiceByInquiry.get(inq.id);
          const steps = buildTimeline(inq, invoice);
          const pkgName = inq.advertising_packages?.tier_label || inq.advertising_packages?.name || 'Package';
          const badge = STATUS_BADGE[inq.status];
          return (
            <Card key={inq.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="text-base">{pkgName}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      Reference <span className="font-mono">#{inq.id.slice(0, 8)}</span> · Submitted {formatDistanceToNow(new Date(inq.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Timeline */}
                <ol className="relative border-l ml-3 space-y-5">
                  {steps.map((s) => {
                    const tone = STEP_TONE[s.state];
                    const Icon = s.icon;
                    return (
                      <li key={s.key} className="ml-6 relative">
                        <span className={`absolute -left-[34px] top-0 inline-flex h-7 w-7 items-center justify-center rounded-full ring-4 ring-background ${tone.bg}`}>
                          <Icon className="h-3.5 w-3.5" />
                        </span>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div>
                            <p className={`text-sm font-medium ${tone.text}`}>{s.label}</p>
                            <p className="text-xs text-muted-foreground">{s.description}</p>
                          </div>
                          {s.at && (
                            <p className="text-xs text-muted-foreground whitespace-nowrap flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {format(new Date(s.at), 'd MMM yyyy, HH:mm')}
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>

                {/* Footer details */}
                <div className="flex flex-wrap items-center gap-2 pt-3 border-t">
                  {invoice && (
                    <Badge variant="outline" className="gap-1 text-[10px]">
                      <Receipt className="h-3 w-3" />
                      {invoice.invoice_number} · {Number(invoice.amount_etb).toLocaleString()} ETB · {invoice.status}
                    </Badge>
                  )}
                  {inq.subscriptions && (
                    <Badge variant="outline" className="text-[10px]">
                      Subscription valid until {format(new Date(inq.subscriptions.expires_at), 'd MMM yyyy')}
                    </Badge>
                  )}
                  {invoice && invoice.status !== 'paid' && invoice.checkout_url && (
                    <Button asChild size="sm" className="ml-auto h-7 text-xs">
                      <a href={invoice.checkout_url} target="_blank" rel="noreferrer">Pay now <ArrowRight className="h-3 w-3 ml-1" /></a>
                    </Button>
                  )}
                  {inq.status === 'won' && (
                    <Button asChild size="sm" variant="ghost" className="ml-auto h-7 text-xs">
                      <Link to="/dashboard/subscription">View credits <ArrowRight className="h-3 w-3 ml-1" /></Link>
                    </Button>
                  )}
                </div>

                {inq.message && (
                  <div className="text-xs bg-muted/50 rounded-md p-3">
                    <p className="font-medium mb-1">Your message</p>
                    <p className="text-muted-foreground italic">{inq.message}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}
